import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Label } from "@shadcn/ui/label";
import { Textarea } from "@shadcn/ui/textarea";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Archive, CalendarClock, Container, PackageCheck, PackageX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePreloadedQuery, useRelayEnvironment } from "react-relay";
import { commitMutation, fetchQuery, graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { hasErrorCode } from "~/components/lock/B2CCheckout/outstandingBalance";
import { formatLockDateTime } from "~/components/lock/lockFormat";
import { getSizeBucket } from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { SummaryRow } from "~/components/warenannahme/SummaryParts";
import { compartmentSide, WarenannahmeDoorFlow } from "~/components/warenannahme/WarenannahmeDoorFlow";
import { originForApp } from "@utils/appOrigin";
import type { deliver_spaceIdCancelMutation } from "./__generated__/deliver_spaceIdCancelMutation.graphql";
import type { deliver_spaceIdConfirmMutation } from "./__generated__/deliver_spaceIdConfirmMutation.graphql";
import type { deliver_spaceIdCreateMutation } from "./__generated__/deliver_spaceIdCreateMutation.graphql";
import type { deliver_spaceIdNoteMutation } from "./__generated__/deliver_spaceIdNoteMutation.graphql";
import type { deliver_spaceIdQuery } from "./__generated__/deliver_spaceIdQuery.graphql";
import type { deliver_spaceIdReadyQuery } from "./__generated__/deliver_spaceIdReadyQuery.graphql";

export const query = graphql`
	query deliver_spaceIdQuery($hubName: String!, $requestId: UUID!, $withRequest: Boolean!) @relay_test_operation {
		deliveryRequest(id: $requestId) @include(if: $withRequest) {
			id
			hubId
			status
			content
			requesterOrganization {
				id
				name
			}
		}
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			city
			postalCode
			hub @required(action: THROW) {
				id
				status
				spaces {
					frame
				}
				availableConfigurations {
					configuration
					spaces {
						id
						frame
						position
						configuration
					}
				}
			}
		}
	}
`;

const readyQuery = graphql`
	query deliver_spaceIdReadyQuery($hubId: UUID!) {
		lockReservations(filter: { subtype: B2B, hubId: $hubId, active: true }, pagination: { page: 0, pageSize: 1000 }) {
			nodes {
				id
			}
		}
	}
`;

const createMutation = graphql`
	mutation deliver_spaceIdCreateMutation($input: LockReservationCreateInput!) {
		b2BLockReservationCreate(input: $input)
	}
`;

const cancelMutation = graphql`
	mutation deliver_spaceIdCancelMutation($id: UUID!) {
		b2BLockReservationDeactivate(id: $id)
	}
`;

const confirmMutation = graphql`
	mutation deliver_spaceIdConfirmMutation($input: GoodsDeliveryConfirmInput!) {
		b2BGoodsDeliveryConfirm(input: $input) {
			id
			deliveredAt
		}
	}
`;

const noteMutation = graphql`
	mutation deliver_spaceIdNoteMutation($input: ReservationNoteSetInput!) {
		reservationNoteSet(input: $input) {
			id
			note
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/warenannahme/deliver/$spaceId")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ requestId: z.string().optional() })),
	loaderDeps: ({ search }) => ({ requestId: search.requestId }),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/warenannahme/deliver` },
			});
		}
		if (!getUserOrganizationId()) {
			throw redirect({ to: "/$hubMetaName/warenannahme/action", params: { hubMetaName: ctx.params.hubMetaName } });
		}
		return preloadQuery<deliver_spaceIdQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			requestId: ctx.deps.requestId ?? NO_REQUEST,
			withRequest: Boolean(ctx.deps.requestId),
		});
	},
});

const READY_TIMEOUT_MS = 20_000;
const NO_REQUEST = "00000000-0000-0000-0000-000000000000";

function RouteComponent() {
	const navigate = useNavigate();
	const environment = useRelayEnvironment();
	const i18n = useI18nContext().LL["new-ui"];
	const { spaceId } = Route.useParams();
	const { hubMeta, deliveryRequest } = usePreloadedQuery<deliver_spaceIdQuery>(query, Route.useLoaderData());
	const request = deliveryRequest?.status === "OPEN" && deliveryRequest.hubId === hubMeta.hub.id ? deliveryRequest : null;
	const [createReservation, isCreating] = useAsyncMutation<deliver_spaceIdCreateMutation>(createMutation);
	const [setNote] = useAsyncMutation<deliver_spaceIdNoteMutation>(noteMutation);
	const [confirmDelivery, isConfirming] = useAsyncMutation<deliver_spaceIdConfirmMutation>(confirmMutation);
	const [cancelDelivery, isCancelling] = useAsyncMutation<deliver_spaceIdCancelMutation>(cancelMutation);
	const [doorClosed, setDoorClosed] = useState(false);

	const [comment, setComment] = useState("");
	const [isPreparing, setIsPreparing] = useState(false);
	const [reservationId, setReservationId] = useState<string | null>(null);
	const [startedAt] = useState(() => new Date().toISOString());
	// The compartment is rented before its door opens. Leaving before asking to open it gives
	// the compartment back, so an abandoned delivery does not block it.
	const unopenedReservation = useRef<string | null>(null);
	useEffect(
		() => () => {
			const id = unopenedReservation.current;
			if (id) commitMutation<deliver_spaceIdCancelMutation>(environment, { mutation: cancelMutation, variables: { id } });
		},
		[environment]
	);

	const space = hubMeta.hub.availableConfigurations.flatMap((config) => config.spaces).find((item) => item.id === spaceId);

	useEffect(() => {
		if (!space && !reservationId) {
			navigate({ to: "/$hubMetaName/warenannahme/deliver", params: { hubMetaName: hubMeta.name }, replace: true });
		}
	}, [space, reservationId, navigate, hubMeta.name]);

	const [frozenSpace] = useState(space);
	const compartment = space ?? frozenSpace;
	if (!compartment) return null;

	const address = `${hubMeta.street}, ${hubMeta.postalCode} ${hubMeta.city}`;
	const size = getSizeBucket(compartment.configuration) ?? "";
	const number = `${compartment.frame}-${compartment.position}`;

	const toOverview = () =>
		navigate({ to: "/$hubMetaName/warenannahme", params: { hubMetaName: hubMeta.name }, search: { done: "delivered" } });

	const handleConfirm = async (id: string) => {
		try {
			await confirmDelivery({ variables: { input: { lockReservationId: id, returnBaseUrl: originForApp("app") } } });
			toOverview();
		} catch (error) {
			console.error(error);
			toast.error(i18n.SOMETHING_WENT_WRONG());
		}
	};

	// The goods did not fit: they were taken out again, so the compartment is given back and
	// the driver chooses another one, still for the same request.
	const handleDoesNotFit = async (id: string) => {
		try {
			await cancelDelivery({ variables: { id } });
			toast.info(i18n.WA_DOES_NOT_FIT_DONE());
			navigate({
				to: "/$hubMetaName/warenannahme/deliver",
				params: { hubMetaName: hubMeta.name },
				search: request ? { requestId: request.id } : {},
				replace: true,
			});
		} catch (error) {
			console.error(error);
			toast.error(i18n.SOMETHING_WENT_WRONG());
		}
	};

	if (reservationId && doorClosed) {
		const isBusy = isConfirming || isCancelling;
		return (
			<PageContainer
				footer={
					<div className="flex flex-col gap-2">
						<Button size="lg" className="w-full shadow-xl" disabled={isBusy} onClick={() => void handleConfirm(reservationId)}>
							<PackageCheck className="size-4" />
							<T>{i18n.WA_DELIVERED_CONFIRM()}</T>
						</Button>
						<Button size="lg" variant="outline" className="w-full" disabled={isBusy} onClick={() => void handleDoesNotFit(reservationId)}>
							<PackageX className="size-4" />
							<T>{i18n.WA_DOES_NOT_FIT()}</T>
						</Button>
					</div>
				}
			>
				<AppHeader service="LOCK_B2B" />
				<div className="flex flex-col gap-4 px-6 pt-6">
					<h1 className="text-2xl font-breit font-medium">
						<T>{i18n.WA_DELIVERED_QUESTION_TITLE()}</T>
					</h1>
					<p className="text-sm text-muted-foreground">
						<T>{i18n.WA_DELIVERED_QUESTION_DESC({ number })}</T>
					</p>
				</div>
			</PageContainer>
		);
	}

	if (reservationId) {
		return (
			<WarenannahmeDoorFlow
				hubId={hubMeta.hub.id}
				hubName={hubMeta.name}
				side={compartmentSide(
					hubMeta.hub.spaces.map((item) => item.frame),
					compartment.frame
				)}
				mode="deliver"
				access={{ type: "owner", reservationId, endRental: false }}
				onOpening={() => {
					unopenedReservation.current = null;
				}}
				onDone={(outcome) => {
					if (outcome === "success") setDoorClosed(true);
				}}
			/>
		);
	}

	const waitUntilReady = async (id: string) => {
		const deadline = Date.now() + READY_TIMEOUT_MS;
		while (Date.now() < deadline) {
			const data = await fetchQuery<deliver_spaceIdReadyQuery>(environment, readyQuery, { hubId: hubMeta.hub.id }).toPromise();
			if (data?.lockReservations.nodes.some((node) => node.id === id)) return true;
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
		return false;
	};

	const handleDeliver = async () => {
		if (hubMeta.hub.status !== "ONLINE") {
			toast.error(i18n.WA_HUB_OFFLINE());
			return;
		}
		setIsPreparing(true);
		try {
			const result = await createReservation({
				variables: {
					input: {
						spaceId: compartment.id,
						flow: "GOODS_RECEPTION",
						deliveryRequestId: request?.id,
					},
				},
			});
			const id = result.b2BLockReservationCreate;
			unopenedReservation.current = id;
			if (!(await waitUntilReady(id))) {
				toast.error(i18n.WA_DELIVER_NOT_READY());
				return;
			}
			if (comment.trim()) {
				await setNote({ variables: { input: { reservationId: id, note: comment.trim() } } });
			}
			setReservationId(id);
		} catch (error) {
			console.error(error);
			toast.error(
				hasErrorCode(error, "HUB_CAPACITY_EXHAUSTED")
					? i18n.WA_SELECT_CAPACITY_EXHAUSTED()
					: hasErrorCode(error, "PRECONDITION_FAILED") && request
						? i18n.WA_REQUEST_NOT_OPEN()
						: i18n.SOMETHING_WENT_WRONG()
			);
		} finally {
			setIsPreparing(false);
		}
	};

	const isBusy = isCreating || isPreparing;

	return (
		<PageContainer
			footer={
				<Button size="lg" className="w-full shadow-xl" disabled={isBusy} onClick={() => void handleDeliver()}>
					<T>{isBusy ? i18n.WA_DELIVERING() : i18n.WA_DELIVER_CTA()}</T>
				</Button>
			}
		>
			<AppHeader service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{i18n.LOCK_SUMMARY_TITLE()}</T>
				</h1>

				<div className="flex flex-col gap-4">
					<p className="text-sm font-breit font-medium">
						<T>{i18n.LOCK_SUMMARY_RENTAL_DETAILS()}</T>
					</p>
					<div className="flex flex-col">
						<SummaryRow
							icon={<CalendarClock className="size-6" />}
							title={i18n.WA_SUMMARY_DELIVERY_ON()}
							description={formatLockDateTime(startedAt)}
						/>
						<SummaryRow icon={<Container className="size-6" />} title={hubMeta.name} description={address} />
						<SummaryRow
							icon={<Archive className="size-6" />}
							title={i18n.LOCK_SUMMARY_COMPARTMENT_NUMBER({ label: size, number })}
							description={i18n.LOCK_SUMMARY_COMPARTMENT_NUMBER_DESC()}
						/>
					</div>
				</div>

				{request ? (
					<div className="flex flex-col gap-2">
						<p className="text-sm font-breit font-medium">
							<T>{i18n.WA_SUMMARY_CONTENT_LABEL()}</T>
						</p>
						<div className="rounded-md border bg-muted px-3 py-2 text-sm whitespace-pre-wrap">{request.content}</div>
						<p className="text-sm text-muted-foreground">
							<T>{i18n.WA_SUMMARY_CONTENT_HELP_REQUEST({ organization: request.requesterOrganization.name })}</T>
						</p>
					</div>
				) : null}

				<div className="flex flex-col gap-2">
					<Label htmlFor="delivery-comment" className="text-sm font-breit font-medium">
						<T>{i18n.WA_SUMMARY_COMMENT_LABEL()}</T>
					</Label>
					<Textarea
						id="delivery-comment"
						rows={3}
						className="field-sizing-fixed"
						value={comment}
						onChange={(event) => setComment(event.target.value)}
						placeholder={i18n.WA_SUMMARY_COMMENT_PLACEHOLDER()}
						aria-describedby="delivery-comment-help"
					/>
					<p id="delivery-comment-help" className="text-sm text-muted-foreground">
						<T>{i18n.WA_SUMMARY_COMMENT_HELP()}</T>
					</p>
				</div>
			</div>
		</PageContainer>
	);
}
